/**
 * My Tickets Section Component
 *
 * Allows users to view and manage their submitted support tickets.
 * Features: view status, add comments, close/delete tickets.
 */

import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Bug,
  Lightbulb,
  HelpCircle,
  LifeBuoy,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  Trash2,
  Loader2,
} from 'lucide-react';
import { useTranslation } from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSupportRequests,
  getSupportComments,
  createSupportComment,
  updateSupportRequest,
  deleteSupportRequest,
} from '@business-assistant/shared/services/supportService';
import type {
  SupportRequest,
  SupportComment,
  SupportRequestType,
  SupportStatus,
} from '@business-assistant/shared/domain/support';

// =============================================================================
// Constants
// =============================================================================

const TYPE_CONFIG: Record<SupportRequestType, { icon: React.ElementType; label: string }> = {
  bug: { icon: Bug, label: 'Bug' },
  feature: { icon: Lightbulb, label: 'Feature Request' },
  support: { icon: LifeBuoy, label: 'Support' },
  question: { icon: HelpCircle, label: 'Question' },
};

const STATUS_CONFIG: Record<SupportStatus, { icon: React.ElementType; label: string; color: string }> = {
  open: { icon: AlertCircle, label: 'Open', color: 'var(--color-warning, #f59e0b)' },
  in_progress: { icon: Clock, label: 'In Progress', color: 'var(--color-primary)' },
  resolved: { icon: CheckCircle, label: 'Resolved', color: 'var(--color-success, #10b981)' },
  closed: { icon: XCircle, label: 'Closed', color: 'var(--color-text-secondary)' },
};

// =============================================================================
// Component
// =============================================================================

interface MyTicketsSectionProps {
  refreshKey?: number;
}

export function MyTicketsSection({ refreshKey }: MyTicketsSectionProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tickets, setTickets] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTicket, setExpandedTicket] = useState<string | null>(null);

  useEffect(() => {
    loadTickets();
  }, [refreshKey]);

  const loadTickets = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    try {
      const result = await getSupportRequests({ userId: user.id }, 50, 0);
      setTickets(result.items);
    } catch (err) {
      console.error('Failed to load tickets:', err);
      setError(t('support.myTickets.loadError') || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseTicket = async (ticketId: string) => {
    try {
      await updateSupportRequest(ticketId, { status: 'closed' });
      setTickets(prev => prev.map(t =>
        t.id === ticketId ? { ...t, status: 'closed' as SupportStatus } : t
      ));
    } catch (err) {
      console.error('Failed to close ticket:', err);
    }
  };

  const handleDeleteTicket = async (ticketId: string) => {
    if (!confirm(t('support.myTickets.confirmDelete') || 'Are you sure you want to delete this ticket?')) {
      return;
    }

    try {
      const result = await deleteSupportRequest(ticketId);
      if (result.success) {
        setTickets(prev => prev.filter(t => t.id !== ticketId));
      } else {
        alert(result.message || 'Failed to delete');
      }
    } catch (err) {
      console.error('Failed to delete ticket:', err);
    }
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="settings-panel">
        <div className="settings-panel-header">
          <MessageSquare size={18} />
          <span>{t('support.myTickets.title') || 'My Tickets'}</span>
        </div>
        <div className="settings-panel-content" style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Loader2 size={24} className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-panel">
      <div className="settings-panel-header">
        <MessageSquare size={18} />
        <span>{t('support.myTickets.title') || 'My Tickets'}</span>
        <button
          onClick={loadTickets}
          style={{
            marginLeft: 'auto',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: 'var(--color-text-secondary)',
          }}
          title={t('common.refresh') || 'Refresh'}
        >
          <RefreshCw size={16} className={loading ? 'spinner' : ''} />
        </button>
      </div>
      <div className="settings-panel-content">
        {error && (
          <div style={{
            padding: '12px',
            background: 'var(--color-error-light, rgba(239, 68, 68, 0.1))',
            color: 'var(--color-error, #ef4444)',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {tickets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-text-secondary)' }}>
            <MessageSquare size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
            <p>{t('support.myTickets.empty') || 'No tickets yet'}</p>
            <p style={{ fontSize: '14px', marginTop: '8px' }}>
              {t('support.myTickets.emptyHint') || 'Use the form above to report an issue'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {tickets.map(ticket => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                expanded={expandedTicket === ticket.id}
                onToggle={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                onClose={() => handleCloseTicket(ticket.id)}
                onDelete={() => handleDeleteTicket(ticket.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Ticket Card Component
// =============================================================================

interface TicketCardProps {
  ticket: SupportRequest;
  expanded: boolean;
  onToggle: () => void;
  onClose: () => void;
  onDelete: () => void;
}

function TicketCard({ ticket, expanded, onToggle, onClose, onDelete }: TicketCardProps) {
  const { t } = useTranslation();
  const [comments, setComments] = useState<SupportComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  const typeConfig = TYPE_CONFIG[ticket.type];
  const statusConfig = STATUS_CONFIG[ticket.status];
  const TypeIcon = typeConfig.icon;
  const StatusIcon = statusConfig.icon;

  useEffect(() => {
    if (expanded) {
      loadComments();
    }
  }, [expanded]);

  const loadComments = async () => {
    setLoadingComments(true);
    try {
      const result = await getSupportComments(ticket.id);
      setComments(result);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSendComment = async () => {
    if (!newComment.trim()) return;

    setSendingComment(true);
    try {
      const comment = await createSupportComment(ticket.id, newComment.trim());
      if (comment) {
        setComments(prev => [...prev, comment]);
        setNewComment('');
      }
    } catch (err) {
      console.error('Failed to send comment:', err);
    } finally {
      setSendingComment(false);
    }
  };

  const canModify = ticket.status !== 'closed' && ticket.status !== 'resolved';

  return (
    <div style={{
      border: '1px solid var(--color-border)',
      borderRadius: '12px',
      overflow: 'hidden',
      background: 'var(--color-bg-secondary)',
    }}>
      {/* Header */}
      <div
        onClick={onToggle}
        style={{
          padding: '16px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}
      >
        <div style={{
          padding: '8px',
          borderRadius: '8px',
          background: 'var(--color-bg-tertiary)',
        }}>
          <TypeIcon size={20} style={{ color: 'var(--color-text-secondary)' }} />
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, marginBottom: '4px' }}>{ticket.subject}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              borderRadius: '12px',
              background: `${statusConfig.color}20`,
              color: statusConfig.color,
            }}>
              <StatusIcon size={12} />
              {statusConfig.label}
            </span>
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {new Date(ticket.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div style={{ borderTop: '1px solid var(--color-border)', padding: '16px' }}>
          {/* Description */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
              {t('support.myTickets.description') || 'Description'}
            </div>
            <p style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>{ticket.description}</p>
          </div>

          {/* Admin Notes (if any) */}
          {ticket.adminNotes && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              background: 'var(--color-primary-light, rgba(59, 130, 246, 0.1))',
              borderRadius: '8px',
              borderLeft: '3px solid var(--color-primary)',
            }}>
              <div style={{ fontSize: '12px', color: 'var(--color-primary)', marginBottom: '4px', fontWeight: 500 }}>
                {t('support.myTickets.adminResponse') || 'Admin Response'}
              </div>
              <p style={{ fontSize: '14px' }}>{ticket.adminNotes}</p>
            </div>
          )}

          {/* Comments */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
              {t('support.myTickets.comments') || 'Comments'} ({comments.length})
            </div>

            {loadingComments ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <Loader2 size={20} className="spinner" />
              </div>
            ) : comments.length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                {t('support.myTickets.noComments') || 'No comments yet'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {comments.map(comment => (
                  <div
                    key={comment.id}
                    style={{
                      padding: '10px 12px',
                      background: comment.isAdmin ? 'var(--color-primary-light, rgba(59, 130, 246, 0.1))' : 'var(--color-bg-tertiary)',
                      borderRadius: '8px',
                      borderLeft: comment.isAdmin ? '3px solid var(--color-primary)' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 500 }}>
                        {comment.isAdmin ? '👤 Support' : comment.userName}
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                        {new Date(comment.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p style={{ fontSize: '13px', margin: 0 }}>{comment.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Comment (if ticket is still open) */}
          {canModify && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={t('support.myTickets.addComment') || 'Add a comment...'}
                className="settings-input"
                style={{ flex: 1 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendComment();
                  }
                }}
              />
              <button
                onClick={handleSendComment}
                disabled={!newComment.trim() || sendingComment}
                className="btn-primary btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {sendingComment ? <Loader2 size={16} className="spinner" /> : <Send size={16} />}
              </button>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            {canModify && (
              <button
                onClick={onClose}
                className="btn-secondary btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <CheckCircle size={14} />
                {t('support.myTickets.closeTicket') || 'Close Ticket'}
              </button>
            )}
            <button
              onClick={onDelete}
              className="btn-secondary btn-sm"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: 'var(--color-error, #ef4444)',
                borderColor: 'var(--color-error, #ef4444)',
              }}
            >
              <Trash2 size={14} />
              {t('support.myTickets.delete') || 'Delete'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyTicketsSection;
